// Формування .xlsx товарно-транспортної накладної з шаблону
// public/templates/ttn.xlsx — «як оригінал»: зберігаються об'єднання комірок,
// межі, ширини колонок і розмітка сторінки, підставляються лише значення полів.
//
// Таблиця «ВІДОМОСТІ ПРО ВАНТАЖ» у шаблоні не має рядків даних (р.41 — заголовки,
// р.42 — нумерація «1..11», р.43 — «Усього:»). ExcelJS.spliceRows не переносить
// об'єднання, тому потрібні рядки вставляються вручну: усе від рядка «Усього:»
// і нижче зсувається на N рядків (комірки + стилі), об'єднання знімаються і
// відновлюються зі зсувом, для N нових рядків будується сітка колонок.

import ExcelJS from 'exceljs';
import {
    TTN_SECTIONS,
    TTN_OPERATION_ROWS,
    TTN_CARGO_COL_SPANS,
    TTN_CARGO_COLUMNS,
    TTN_CARGO_NUMBERS_ROW,
    TTN_CARGO_TOTALS_ROW,
    TTN_CARGO_TOTALS_CELLS,
    formatUaDate,
    TtnFields,
    TtnCargoRow,
} from './ttnTemplate';
import { uaHryvniaToWords } from './uaAmountToWords';

const XLSX_MIME =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const TEMPLATE_URL = (process.env.PUBLIC_URL || '') + '/templates/ttn.xlsx';

const LAST_ROW = 63;   // A1:BS63 у шаблоні
const COL_COUNT = 71;  // A..BS

const THIN = { style: 'thin' as const, color: { argb: 'FF000000' } };
const GRID_BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };

// "1 200,50" -> 1200.5 ; порожнє / нечисло -> 0
export const toNum = (raw: any): number => {
    if (typeof raw === 'number') return raw;
    const s = String(raw ?? '').replace(/\s/g, '').replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
};

const splitCell = (ref: string): { col: string; row: number } => {
    const m = /^([A-Z]+)(\d+)$/.exec(ref);
    if (!m) throw new Error('Некоректна адреса комірки: ' + ref);
    return { col: m[1], row: parseInt(m[2], 10) };
};

const joinNonEmpty = (parts: Array<string | false | undefined>, sep: string) =>
    parts.filter((p) => p && String(p).trim()).join(sep);

export const cargoRowHasData = (r: TtnCargoRow): boolean =>
    TTN_CARGO_COLUMNS.some((c) => !c.auto && String(r[c.key] ?? '').trim() !== '');

// Шаблон вантажиться один раз; для кожного виклику віддаємо свіжу копію
// (ExcelJS.load «поглинає» буфер).
let templateCache: ArrayBuffer | null = null;
const loadTemplate = async (): Promise<ArrayBuffer> => {
    if (!templateCache) {
        const res = await fetch(TEMPLATE_URL);
        if (!res.ok) {
            throw new Error('Не вдалося завантажити шаблон ТТН (' + res.status + ')');
        }
        templateCache = await res.arrayBuffer();
    }
    return templateCache.slice(0);
};

export const buildTtnXlsx = async (fields: TtnFields): Promise<Blob> => {
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await loadTemplate());
    const ws = wb.worksheets[0];

    // У шаблоні ttn.xlsx рядки 14–15 («Місце де зберігається автомобіль *» та
    // його пояснення) залиті жовтим — це робоча позначка, яка не має потрапляти
    // у готову накладну / pdf. Знімаємо заливку.
    for (let r = 14; r <= 15; r++) {
        const row = ws.getRow(r);
        for (let c = 1; c <= COL_COUNT; c++) {
            row.getCell(c).fill = { type: 'pattern', pattern: 'none' };
        }
    }

    const get = (k: string) => String((fields as any)[k] ?? '').trim();
    const cargo = (fields.cargo || []).filter(cargoRowHasData);
    const n = cargo.length;

    const placesTotal = cargo.reduce((s, r) => s + toNum(r.places), 0);
    const sumTotal = cargo.reduce((s, r) => s + toNum(r.sumWithVat), 0);
    const weightTotal = cargo.reduce((s, r) => s + toNum(r.grossWeight), 0);

    const combinedDriver = joinNonEmpty(
        [get('driverName'), get('driverLicence') && '№ ' + get('driverLicence')], ', ',
    );
    const combinedShipper = joinNonEmpty(
        [get('shipperName'), get('shipperCode') && 'код ЄДРПОУ ' + get('shipperCode')], ', ',
    );
    const combinedConsignee = joinNonEmpty(
        [get('consigneeName'), get('consigneeCode') && 'код ЄДРПОУ ' + get('consigneeCode')], ', ',
    );

    const valueFor = (key: string): string => {
        switch (key) {
            case 'date': return formatUaDate(get('date'));
            case 'driverName': return combinedDriver;
            case 'shipperName': return combinedShipper;
            case 'consigneeName': return combinedConsignee;
            case 'totalSumWords':
                return get('totalSumWords') || uaHryvniaToWords(sumTotal);
            default: return get(key);
        }
    };

    // --- Поля шапки (рядки < 43 — до таблиці вантажу) + збір полів нижче ------
    const belowFields: Array<[string, string]> = [];
    TTN_SECTIONS.forEach((section) =>
        section.fields.forEach((f) => {
            if (!f.cell) return; // допоміжні поля без власної комірки
            const v = valueFor(f.key);
            if (splitCell(f.cell).row < TTN_CARGO_TOTALS_ROW) {
                ws.getCell(f.cell).value = v === '' ? null : v;
            } else {
                belowFields.push([f.cell, v]);
            }
        }),
    );

    // --- Вставка N рядків вантажу перед рядком «Усього:» --------------------
    const FIRST = TTN_CARGO_TOTALS_ROW; // 43
    if (n > 0) {
        const merges: string[] = ((ws.model as any).merges || []).slice();

        merges.forEach((range) => {
            if (splitCell(range.split(':')[0]).row >= FIRST) ws.unMergeCells(range);
        });

        // зсув комірок + стилів знизу вгору
        for (let r = LAST_ROW; r >= FIRST; r--) {
            const src = ws.getRow(r);
            const dst = ws.getRow(r + n);
            dst.height = src.height;
            for (let c = 1; c <= COL_COUNT; c++) {
                const s = src.getCell(c);
                const d = dst.getCell(c);
                d.style = s.style;
                d.value = s.value === undefined ? null : s.value;
            }
        }

        // проби стилю (шрифт) з рядка нумерації колонок
        const protoFont =
            ws.getCell(`D${TTN_CARGO_NUMBERS_ROW}`).font ||
            { name: 'Times New Roman', size: 8 };

        for (let i = 0; i < n; i++) {
            const r = FIRST + i;
            const row = ws.getRow(r);
            row.height = 14;
            for (let c = 1; c <= COL_COUNT; c++) {
                const cell = row.getCell(c);
                cell.value = null;
                cell.style = {};
            }
            TTN_CARGO_COL_SPANS.forEach(([s, e], ci) => {
                ws.mergeCells(`${s}${r}:${e}${r}`);
                const colDef = TTN_CARGO_COLUMNS[ci];
                const anchor = ws.getCell(`${s}${r}`);
                anchor.style = {
                    font: protoFont,
                    border: GRID_BORDER,
                    alignment: {
                        vertical: 'top',
                        horizontal: colDef.auto || colDef.numeric ? 'center' : 'left',
                        wrapText: true,
                    },
                };
                let v: string | number | null;
                if (colDef.auto) {
                    v = i + 1;
                } else if (colDef.numeric) {
                    const raw = cargo[i][colDef.key];
                    v = raw != null && String(raw).trim() !== '' ? toNum(raw) : null;
                } else {
                    v = cargo[i][colDef.key] || null;
                }
                anchor.value = v;
            });
        }

        // відновлення об'єднань зі зсувом
        merges.forEach((range) => {
            const [tl, br] = range.split(':');
            const a = splitCell(tl);
            const b = splitCell(br);
            if (a.row >= FIRST) {
                ws.mergeCells(`${a.col}${a.row + n}:${b.col}${b.row + n}`);
            }
        });
    }

    // --- Поля нижче таблиці вантажу (зі зсувом на N) -----------------------
    const shift = (ref: string) => {
        const { col, row } = splitCell(ref);
        return `${col}${row + n}`;
    };

    belowFields.forEach(([ref, v]) => {
        ws.getCell(shift(ref)).value = v === '' ? null : v;
    });

    TTN_OPERATION_ROWS.forEach((opRow) =>
        opRow.cols.forEach((c) => {
            const v = get(c.key);
            if (v) ws.getCell(shift(c.cell)).value = v;
        }),
    );

    const totalsByKey: { [k: string]: number } = {
        places: placesTotal,
        sumWithVat: sumTotal,
        grossWeight: weightTotal,
    };
    Object.entries(TTN_CARGO_TOTALS_CELLS).forEach(([key, ref]) => {
        if (totalsByKey[key]) ws.getCell(shift(ref)).value = totalsByKey[key];
    });

    const out = await wb.xlsx.writeBuffer();
    return new Blob([out], { type: XLSX_MIME });
};

// Скачування Blob під заданим ім'ям (без file-saver).
export const downloadBlob = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};
